import React, { useEffect, useState } from 'react';
import { styled, Text, TokenTextAppearance, TextTag } from "@volkswagen-onehub/components-core";
import { Model } from "./Model";
import { getLang } from '../../translations';
import { Filters } from "../../types/filters";
import { isDisabled } from '../../utils/is-model-option-disabled';
import { order, Order } from '../../utils/type-has-inventory';
import { useFeatureServices } from '../../use-feature-services';
import { createConstantData, createPartner, createProcess } from '../../utils/get-tracking-event-data';
import { getSingleSelectedDealer } from '../../utils/get-single-selected-dealer';
import useFiltersQuery from '../../api/use-filters-query';
import { FiltersResponse, FiltersResponseKey, ModelFilterOption } from '../../types/globalapi/filters';
import { ResponseSelector } from '../../types/globalapi/response-selector';
import { ModelLoader } from './ModelLoader';
import Skeleton from 'react-loading-skeleton';
import { useAppliedFilterStore } from '../../store/filtersv2';
import { useFiltering } from '../../utils/use-filtering';

type Props = {
  expanded?: boolean;
  header: string;
  type: string;
}

const SetCmp = styled.div`
  margin-bottom: 64px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const ModelsContainer = styled.div`
  margin-top: 24px;
  max-width: 1750px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(max(300px, 22.2222%), 1fr));
  gap: 48px;
`;

export const ModelSet = ({
  header,
  type
}:  Props) => {
  const lang = getLang();

  const getModelOptions: ResponseSelector<FiltersResponse, ModelFilterOption[]> = res => res[FiltersResponseKey.MODEL];
  const { isLoading, data: models } = useFiltersQuery({select: getModelOptions});
  const [ modelsDisplayed, setModelsDisplayed ] = useState<ModelFilterOption[]>([]);
  const { applied } = useAppliedFilterStore();
  const { applyQuickFilter } = useFiltering()
  const { category, vehicle_type } = applied;
  const selection: string[] = applied.model as [];
  const { tracking } = useFeatureServices();

  const modelOrder = order[type as keyof Order];
  const partner = getSingleSelectedDealer();

  const handleClick = (e: React.SyntheticEvent<HTMLInputElement>, model_name: string) => {
    const { name, value, checked } = e.currentTarget;
    const updatedSelection = selection?.includes(value)
    ? selection.filter((item) => item !== value)
    : [...selection, value];
    applyQuickFilter(name as keyof Filters, updatedSelection);
    if (checked) {
      tracking.track('VWBasic_VehicleButton_Click', {
        ...createConstantData(),
        configuration: {
          Manufacturer: "Volkswagen",
          ModelName: model_name,
        },
        partner: createPartner(partner),
        process: createProcess("1B"),
        link: {
          url: window.location.href,
          name: model_name
        }
      })
    }
  };

  useEffect(() => {
    if (models) {
      const vehicles: ModelFilterOption[] = [];
      modelOrder.forEach((orderItem) =>
        (models as ModelFilterOption[]).forEach((model: ModelFilterOption) => {
          if (orderItem === model.value && (vehicle_type?.length === 0 || model.vehicle_types.filter(item => vehicle_type?.includes(item)).length)) {
            vehicles.push(model);
          }
        })
      );
      setModelsDisplayed(vehicles);
    }
  }, [models, vehicle_type])

  return (
    isLoading
    ?
    <SetCmp>
      <Skeleton width="100" height="36" borderRadius="0"/>
      <ModelsContainer>
        { modelOrder.map((i) => <ModelLoader key={i} />) }
      </ModelsContainer>
    </SetCmp>
    : modelsDisplayed.length ? <SetCmp>
      <Text tag={TextTag.h2} appearance={TokenTextAppearance.headline300} bold>{ header }</Text>
      <ModelsContainer>
        {
          modelsDisplayed.map((
            model: ModelFilterOption,
            i: number
          ) => {
            const key = `model-${model.value}-${i}`;
            return <Model
              key={key}
              name="model"
              label={model.label[lang]}
              value={model.value}
              image={model.image}
              alt={model.alt[lang]}
              onClick={(e) => handleClick(e, model.label[lang])}
              checked={selection.includes(model.value)}
              color={model.background_color}
              disabled={isDisabled(model, category as string[])}
            />
          })
        }
      </ModelsContainer>
    </SetCmp>
    : null
  );
};
